import React, { useEffect, useMemo, useRef, useState } from 'react';
import CleanedHtml from 'shared/components/CleanedHtml';
import { LATE_LOAD_EVENTS_SOURCE, wrapWidgetScriptV2 } from 'shared/lib/widgetScriptRuntime';
import { scopeCss } from 'shared/lib/widgetCode/scopeCss';
import { scopeCssLegacyAi } from 'shared/lib/widgetCode/legacyScopers';
import schema from './props-schema';
import { onGenesisWithSession } from 'shared/lib/genesisOrigin';
import type { AiGeneratedContent, Props } from './props-schema';

type Widget<P> = React.FC<P> & { schema?: unknown };

const BASE_SCOPE_CLASS = 'ai-widget-root';

const sanitizeScopeClass = (value?: string): string => {
  if (!value) {
    return `${BASE_SCOPE_CLASS}-${Date.now().toString(36)}`;
  }
  const cleaned = value.replace(/[^a-zA-Z0-9-_]/g, '-');
  return cleaned.length ? cleaned : `${BASE_SCOPE_CLASS}-${Date.now().toString(36)}`;
};

export const wrapScript = (js: string, scopeClass: string): string => {
  const trimmed = js.trim();
  if (!trimmed) {
    return '';
  }
  return [
    '(function(){',
    LATE_LOAD_EVENTS_SOURCE,
    `  const scopeSelector = '.${scopeClass}';`,
    '  const root = document.querySelector(scopeSelector);',
    '  if (!root) { return; }',
    '  var __gxRestoreLoadEvents = __gxPatchLateLoadEvents();',
    '  try {',
    `    ${trimmed}`,
    '  } catch (error) {',
    "    console.error('AI widget script failed', error);",
    '  } finally {',
    '    __gxRestoreLoadEvents();',
    '  }',
    '})();'
  ].join('\n');
};

// `_id` is the component id from the page, passed in by the slot.
const AiGeneratedWidget: Widget<Props & { _id?: string }> = ({ content, _id }) => {
  // Always initialize values so Hooks are never conditional
  const html = (content as AiGeneratedContent)?.html ?? '';
  const css = (content as AiGeneratedContent)?.css ?? '';
  const js = (content as AiGeneratedContent)?.js ?? '';
  const providedScope = (content as AiGeneratedContent)?.scopeClass;
  // Code pasted with the v2 tools is scoped by the v2 scoper; everything else as before.
  const isV2 = (content as AiGeneratedContent)?.scoper === 2;
  const instanceId = isV2 && _id ? String(_id).replace(/[^\w-]/g, '') : '';

  const sanitizedScope = useMemo(
    () => sanitizeScopeClass(providedScope),
    [providedScope]
  );

  const scopedCss = useMemo(
    () => (isV2 ? scopeCss(css, sanitizedScope).css : scopeCssLegacyAi(css, sanitizedScope)),
    [css, sanitizedScope, isV2]
  );

  const scriptContent = useMemo(
    () => (isV2
      ? wrapWidgetScriptV2({
        js,
        // Each copy of the widget on a page finds its own root.
        rootSelector: instanceId ? `[data-gx-instance="${instanceId}"]` : `.${sanitizedScope}`,
        label: 'AI widget'
      })
      : wrapScript(js, sanitizedScope)),
    [js, sanitizedScope, isV2, instanceId]
  );

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const styleId = `ai-widget-style-${sanitizedScope}`;
    const prevStyle = document.getElementById(styleId);
    if (prevStyle) {
      prevStyle.remove();
    }

    if (!scopedCss) {
      return;
    }

    const style = document.createElement('style');
    style.id = styleId;
    style.dataset.aiWidget = sanitizedScope;
    style.textContent = scopedCss;
    document.head.appendChild(style);

    return () => {
      style.remove();
    };
  }, [scopedCss, sanitizedScope]);

  // Written by an AI app through the connector: its script doesn't run on Genesis with a session
  // (editor, previews, reviews) until a person keeps it in the editor. Sandboxed previews and the live site run it.
  // Decided after mounting (the server render can't know where it will run), so hydration matches.
  const fromApp = Boolean((content as AiGeneratedContent)?.via);
  const [holdScript, setHoldScript] = useState<boolean | null>(null);
  useEffect(() => {
    setHoldScript(fromApp && Boolean(scriptContent) && onGenesisWithSession());
  }, [fromApp, scriptContent]);

  useEffect(() => {
    if (!scriptContent || holdScript !== false) {
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.dataset.aiWidget = sanitizedScope;
    script.text = scriptContent;
    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, [scriptContent, sanitizedScope, holdScript]);

  // Also written by an AI app, and shown by Genesis to someone signed in (render() in cms.tsx sets this):
  // the server can't sanitize HTML, so it sends none, and the browser adds it through DOMPurify once the
  // page has loaded, as the editor shows it. Nothing in it can then run as the person viewing it.
  const deferHtml = Boolean((content as AiGeneratedContent)?.deferHtml);
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const className = `${BASE_SCOPE_CLASS} ${sanitizedScope}`.trim();
  const trimmedHtml = html.trim();

  if (!trimmedHtml) {
    return (
      <div className={className} ref={containerRef} style={{
        border: '1px dashed rgba(15, 23, 42, 0.25)',
        borderRadius: 16,
        padding: '32px',
        background: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 220,
        color: '#475569',
        textAlign: 'center',
        gap: '12px'
      }}>
        <span style={{
          fontWeight: 600,
          fontSize: 16,
          letterSpacing: '0.04em',
          textTransform: 'uppercase'
        }}>AI Generated Widget</span>
        <p style={{ maxWidth: 360, margin: 0, lineHeight: 1.6 }}>
          This placeholder disappears as soon as you edit the widget.
        </p>
      </div>
    );
  }

  return (
    <div className={className} ref={containerRef} data-gx-instance={instanceId || undefined}>
      {holdScript && (
        // Inside the section (the review screen counts a slot's children), and immune to its CSS.
        <div role="note" data-gx-script-held="" style={{
          all: 'initial', display: 'block', boxSizing: 'border-box', width: '100%', padding: '6px 12px',
          font: '500 12px/1.4 -apple-system, "Segoe UI", Roboto, sans-serif',
          color: '#6B4520', background: '#FBF3EA', borderBottom: '1px solid #E8D5C0'
        }}>
          {`This section’s script was written by ${(content as AiGeneratedContent)?.via || 'an AI app'}, so it doesn’t run here. It runs in the full preview the AI app links to and on the live site; keep it in the section editor to run it here.`}
        </div>
      )}
      <CleanedHtml as="div">
        {deferHtml && !mounted ? '' : trimmedHtml}
      </CleanedHtml>
    </div>
  );
};

AiGeneratedWidget.schema = schema;

export default AiGeneratedWidget;
